import { Field, Label, Radio, RadioGroup } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon, CalendarIcon } from '@heroicons/react/16/solid';
import { CheckCircleIcon, InformationCircleIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { deepEqual } from 'fast-equals';
import { Fragment, useMemo, useState } from 'react';

import { Empty } from '@/components/UI/Empty';
import Skeleton from '@/components/UI/Skeleton';

import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { get } from '@/renderer/utils/get';
import { useDebounce } from '@/renderer/utils/hooks/useDebounce';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { useLocale } from '@/renderer/utils/locale';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import { CrytpoIcon } from './components/CryptoIcon';
import { PriceInfo } from './components/PriceInfo';
import { BettingTalkIcon, CBBIcon, CFBIcon, NFLIcon } from './components/ProductIcons';
import ProductMenu from './components/ProductMenu';
import StripeButton from './components/StripeButton';
import type { PriceCalculator } from './components/calculatePrice';
import calculatePrice from './components/calculatePrice';
import {
  OFFER_MATRIX_KEYS,
  OFFER_MATRIX_MAP,
  OFFER_MATRIX_TITLES,
  PRODUCT_DEFAULT_DESCRIPTION,
  matrixFromOffers,
  sportIdFromName,
} from './components/productHelpers';

type MPO = API.MembershipProductOffer;

const getProducts: RequestSchema<
  API.MembershipProduct<true>[], // actually API.WithPage but useRequest only returns the `data` field
  API.PageInput & API.WithData<{ ids?: number[] }>
> = {
  input: '/api/v1/product',
};

const frequencies = [
  { value: 'monthly', label: 'Monthly', priceSuffix: '/month' },
  { value: 'annually', label: 'Annually', priceSuffix: '/year' },
];

const BASE_STATS: API.MembershipProductStats = {
  canceled: 0,
  loss: 0,
  noAction: 0,
  pending: 0,
  played: 0,
  push: 0,
  units: 0,
  win: 0,
  buyable: 0,
  charged: 0,
  roi: 0,
  total: 0,
};

const BASE_OFFER: MPO = {
  id: 0,
  ids: [0],
  plays: 0,
  price: 0,
};

const disclaimer = {
  href: '#',
  summary:
    'It should be expected that lines will move within seconds of plays being released. Despite our long-term history of winning (55-56% across thousands of plays), we are not immune to variance, poor streaks, or losing seasons. Please manage your bankroll and expectations accordingly. Releases may be viewed as "sharp" action that could lead to reduced limits or account closure at certain sportsbooks. Proper bankroll size, available outs, time and skills are needed to be successful and justify the cost of this service.',
};

const ProductPage: React.FC<{ tag?: string }> = ({ tag: givenTag }) => {
  const pageContext = usePageContext();
  const { locale, formatNumber } = useLocale();

  const balance = useMe()?.balance || 0;
  const discount = useMe()?.discount || 0;

  const tag = pageContext.routeParams.tag || givenTag;

  const opts = useMemo(
    () => ({
      skipRefresh: true,
      query: {
        data: {
          id: pageContext.routeParams.sportId ? undefined : pageContext.routeParams.productId,
          sportId: sportIdFromName(pageContext.routeParams.sportId),
          'tags$@>': tag && [tag],
        },
        page: {
          limit: 100,
        },
      },
    }),
    [pageContext.routeParams.productId, pageContext.routeParams.sportId, tag],
  );
  const productsRequest = useRequest(getProducts, opts);
  const isLoading = useDebounce(productsRequest.loading, 300);

  const products = productsRequest.data;
  const error = productsRequest.error;

  const product = useMemo(() => {
    if (products) {
      if (pageContext.routeParams.productId) {
        return (
          products.find((prod) => `${prod.id}` === `${pageContext.routeParams.productId}`) ||
          products[0]
        );
      }

      if (pageContext.routeParams.sportId) {
        let best: API.MembershipProduct<true> | undefined = undefined;
        const sportId = sportIdFromName(pageContext.routeParams.sportId);

        if (typeof sportId === 'number') {
          for (const prod of products) {
            if (prod.sportIds?.includes(sportId)) {
              if (!best) {
                best = prod;
              }

              if (prod.sportIds.length === 1) {
                best = prod;
                break;
              }
            }
          }

          return best || products[0];
        }
      }

      if (tag) {
        let best: API.MembershipProduct<true> | undefined = undefined;

        for (const prod of products) {
          if (prod.tags?.includes(tag)) {
            if (!best) {
              best = prod;
            }

            if (prod.tags.length === 1) {
              best = prod;
              break;
            }
          }
        }

        return best || products[0];
      }
    }

    return products?.[0];
  }, [products, pageContext.routeParams.productId, pageContext.routeParams.sportId, tag]);

  const offers = product?.offers;

  const urlSelectedIndex = useMemo(() => {
    const key = pageContext?.urlParsed?.search?.key;

    if (key) {
      const foundIdx = product?.offers.findIndex((productOffer) => productOffer.key === key);

      if (foundIdx !== undefined) {
        return foundIdx;
      }
    }

    return 0;
  }, [offers, pageContext?.urlParsed?.search?.key]);

  const stats = useMemo(() => {
    if (product?.stats) {
      return {
        ...product.stats,
        played:
          product.stats.played ||
          (+product.stats.loss || 0) +
            (+product.stats.pending || 0) +
            (+product.stats.push || 0) +
            (+product.stats.win || 0) +
            (-product.stats.uncharged || 0),
      };
    }

    return undefined;
  }, [product?.stats]);

  const [selectedIndex, setSelectedIndex] = useState<number>(urlSelectedIndex);
  const [frequency, setFrequency] = useState(frequencies[0]);

  const variety = offers?.[selectedIndex];

  const calcPrice = useMemo(() => {
    return calculatePrice({
      discount,
      stats: stats || BASE_STATS,
      variety: variety || BASE_OFFER,
    });
  }, [discount, stats, variety]);

  const getBestOfferMatch = useMemo(() => {
    return (
      key: keyof MPO,
      map: Map<string, { option: MPO[keyof MPO]; indexes: Set<number> }>,
      next: string,
      proposedOffer?: MPO,
    ) => {
      const matrix = map.get(next);
      const nextAllowedIndexes = Array.from(matrix?.indexes || []);

      if (nextAllowedIndexes.length === 0) {
        return 0;
      }

      if (nextAllowedIndexes.length === 1) {
        return nextAllowedIndexes[0];
      }

      let bestMatch = 0;
      let bestMatchCount = -1;

      if (offers) {
        for (const offerIdx in offers) {
          const offer = offers[offerIdx];
          const value = get(offer, key);

          if (
            value === matrix?.option ||
            (typeof value === 'object' && deepEqual(value, matrix?.option))
          ) {
            let matchCount = 0;

            if (!proposedOffer) {
              return +offerIdx;
            }

            for (const matrixKey of OFFER_MATRIX_KEYS.values()) {
              const offerValue = get(offer, matrixKey);
              const proposedValue = get(proposedOffer, matrixKey);

              if (
                offerValue === proposedValue ||
                (typeof offerValue === 'object' && deepEqual(offerValue, proposedValue))
              ) {
                matchCount++;
              }
            }

            if (matchCount > bestMatchCount) {
              bestMatchCount = matchCount;
              bestMatch = +offerIdx;
            }
          }
        }
      }

      return bestMatch;
    };
  }, [offers]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(product): We want this to run when product changes
  useUpdateEffect(() => {
    setSelectedIndex(0);
  }, [product]);

  const triggerSetSelections = useMemo(() => {
    return (key: keyof MPO, map: Map<string, { option: MPO[keyof MPO]; indexes: Set<number> }>) =>
      (next: string) => {
        setSelectedIndex(getBestOfferMatch(key, map, next, variety));
      };
  }, [variety, getBestOfferMatch]);

  const matrices = useMemo(() => {
    return matrixFromOffers(product);
  }, [product]);

  const hasBettingTalkExtra = useMemo(() => {
    const apid = variety?.addOn?.productId;

    if (apid) {
      return product?.products?.[apid]?.tags?.includes('betting-talk') || apid === 49;
    }

    if (variety?.combos) {
      for (const { productId: pid } of variety.combos) {
        if (pid) {
          return product?.products?.[pid]?.tags?.includes('betting-talk') || pid === 49;
        }
      }
    }
  }, [variety, product?.products]);

  const IconComponent = useMemo(() => {
    switch (sportIdFromName(pageContext.routeParams.sportId) || product?.sportIds?.[0]) {
      case 1:
        return CFBIcon;

      case 2:
        return CBBIcon;

      case 3:
        return NFLIcon;
    }

    if (product?.tags?.includes('betting-talk')) {
      return BettingTalkIcon;
    }

    // @todo better default icon?
    return CFBIcon;
  }, [pageContext.routeParams.sportId, product?.sportIds, product?.tags]);

  /** Something of a sprintf-type function for templating strings */
  const nestedReplace = useMemo(() => {
    return function nestedReplaceInner<
      O,
      M extends Map<string, unknown>,
      P extends PriceCalculator,
    >(str: string, option?: O, matrix?: M, calc?: P) {
      let cur = str;
      let next = str;

      do {
        cur = next;
        next = next?.replace(
          /{([a-zA-Z0-9_-]+)([.[\]]([a-zA-Z0-9[\]._|"'-]+))?}/g,
          (match, m1, deeper, deepest) => {
            const path = deeper?.[0] === '.' ? deepest : deeper;

            const split = match.split('||');

            if (split.length > 1) {
              for (const part of split) {
                const l = part[0];
                const start = l === '{' ? 1 : 0;
                const r = part[part.length - 1];
                const end = r === '}' ? part.length - 2 : part.length - 1;

                if (part[start] === part[end] && part[start] === '"') {
                  return part.slice(start + 1, end);
                }

                const corrected = `${l === '{' ? '' : '{'}${part}${r === '}' ? '' : '}'}`;
                const ret = nestedReplaceInner(corrected, option, matrix, calc);

                if (ret !== undefined && ret !== null && ret !== corrected) {
                  return ret;
                }
              }

              return match;
            }

            // There's probably a better way than this
            const someCalc = calc || calcPrice;

            if (path && option && m1 === 'option') {
              return get(option, path, match);
            } else if (path && variety && m1 === 'variety') {
              return get(variety, path, match);
            } else if (path && product && m1 === 'product') {
              return get(product, path, match);
            } else if (path && products && m1 === 'products') {
              return get(products, path, match);
            } else if (path && someCalc && m1 === 'calc') {
              if (path === 'plays' && someCalc.plays > 300) {
                // special case, when an offer has more than 300 plays it's probably meant to be hidden
                return '';
              }

              return get(someCalc, path, match);
            } else if (path && matrix && m1 === 'matrix') {
              return get(Array.from(matrix.values()), path, match);
            } else if (m1 === 'this') {
              return path ? get(option, path, match) : option;
            } else if (option) {
              return get(option, m1, match);
            }

            return match;
          },
        );
      } while (next !== cur);

      return next;
    };
  }, [calcPrice, product, products, variety]);

  return (
    <div className="dark:bg-zinc-800">
      <Header
        // title={product?.title ? `Order ${product?.title}` : 'Order'}
        title="Order"
        className="py-2 sm:hidden"
      />
      <ProductMenu
        products={products}
        product={product}
        isLoading={isLoading}
        routeParams={pageContext.routeParams}
      />
      <div>
        <div className="mx-auto px-4 py-6 md:p-8 lg:max-w-7xl">
          {pageContext.urlParsed?.search?.source && (
            <div className="mb-8 flex flex-row items-center justify-start space-x-2 rounded bg-amber-50 p-4 text-amber-700">
              <LockClosedIcon className="size-6 shrink-0" />
              <span className="font-medium text-sm sm:text-base">
                You must be a Betting Talk member to view that thread. Join below.
              </span>
            </div>
          )}
          {/* Product */}
          {!isLoading &&
            !products?.length &&
            (error ? (
              <Empty
                IconComponent={IconComponent}
                title="We ran into an error fetching our products. Please try again later."
                description={`Error message: ${error.message}.`}
              />
            ) : (
              <Empty
                IconComponent={IconComponent}
                title="No Products Found"
                description="We're sorry, we aren't currently offering any products that match your search."
              />
            ))}
          {((!products && isLoading) || products?.length > 0) && (
            <div className="grid grid-flow-dense lg:auto-rows-min lg:grid-cols-7 lg:gap-x-8 xl:gap-x-16">
              <div className="mx-auto w-full max-w-2xl lg:col-span-4 lg:row-span-1 lg:mt-0 lg:max-w-none">
                {/* Product details */}
                <div className="rounded p-4 shadow ring-1 ring-emerald-900/10 dark:bg-white/5 sm:p-10 dark:ring-white/10">
                  <div className="mx-auto mt-4 max-w-2xl lg:col-span-3 lg:row-span-2 lg:mt-0 lg:max-w-none">
                    <div className="flex flex-col">
                      <div className="flex flex-row items-center">
                        <IconComponent className="mr-4 hidden size-10 shrink-0 fill-zinc-800 stroke-2 sm:inline-block dark:fill-white" />
                        <h1 className="font-bold text-2xl text-zinc-900 tracking-tight dark:text-zinc-100 sm:text-3xl">
                          <Skeleton loading={isLoading}>{product?.title || 'Order'}</Skeleton>
                        </h1>
                      </div>

                      {product?.ui?.href && (
                        <a
                          href={product?.ui?.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex w-fit items-center space-x-1 rounded-full bg-zinc-900/10 px-4 py-2 text-xs text-zinc-700 dark:bg-white/20 hover:bg-zinc-900/15 dark:text-white hover:text-zinc-900"
                        >
                          <span>{product?.ui?.href}</span>
                          <ArrowTopRightOnSquareIcon
                            className="size-4 shrink-0"
                            aria-hidden="true"
                          />
                        </a>
                      )}

                      <section aria-labelledby="information-heading" className="mt-3 sm:mt-4">
                        <h2 id="information-heading" className="sr-only">
                          Product information
                        </h2>

                        <div className="mb-6 font-normal text-sm text-zinc-500 tracking-tight sm:mb-10 sm:font-medium dark:text-zinc-100 xl:text-base">
                          <h3>
                            <Skeleton loading={isLoading}>
                              {nestedReplace(
                                product?.includesDescription ||
                                  product?.description ||
                                  PRODUCT_DEFAULT_DESCRIPTION[
                                    variety?.addOn
                                      ? variety?.combos?.length
                                        ? 'addOnCombos'
                                        : 'addOn'
                                      : variety?.combos?.length
                                        ? 'combos'
                                        : 'default'
                                  ],
                              )}
                            </Skeleton>
                          </h3>
                          {hasBettingTalkExtra && (
                            <h3 className="mt-2">
                              +{' '}
                              <span className="text-sky-800 dark:text-sky-200">
                                12-month Betting Talk Membership (or extension)
                              </span>
                            </h3>
                          )}
                        </div>
                      </section>
                    </div>

                    {(variety?.ui?.frequencyEnabled || product?.ui?.frequencyEnabled) && (
                      <RadioGroup
                        value={frequency}
                        onChange={setFrequency}
                        aria-label="Payment frequency"
                        className="mt-4 grid w-fit grid-cols-2 gap-x-1 rounded-full p-1 text-center font-semibold text-xs leading-5 ring-1 ring-gray-200 ring-inset"
                      >
                        {frequencies.map((option) => (
                          <Field key={option.value} className="flex items-center gap-2">
                            <Radio as={Fragment} value={option}>
                              {({ checked }) => (
                                <span
                                  className={clsx(
                                    'w-full cursor-pointer rounded-full px-2.5 py-1',
                                    checked
                                      ? 'bg-emerald-400/20 text-emerald-800'
                                      : 'text-zinc-500',
                                  )}
                                >
                                  {option.label}
                                </span>
                              )}
                            </Radio>
                          </Field>
                        ))}
                      </RadioGroup>
                    )}

                    {!!matrices &&
                      Array.from(matrices).map(([key, matrix]) => {
                        let lastPrice: string | number = Infinity;
                        let curPriceIndex = 0;
                        return (
                          <RadioGroup
                            key={key} // @todo matrix id
                            value={`${JSON.stringify(get(variety, key))}`}
                            onChange={triggerSetSelections(
                              key as keyof typeof OFFER_MATRIX_MAP,
                              matrix,
                            )}
                            aria-label={key}
                            className="group mb-6 sm:mb-10"
                            // className="mt-4 grid w-fit grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200"
                          >
                            <Label className="w-full truncate font-medium text-xs text-zinc-800 dark:text-zinc-200">
                              {product?.ui?.text?.[key] ||
                                OFFER_MATRIX_TITLES[key as keyof typeof OFFER_MATRIX_TITLES] ||
                                key}
                            </Label>

                            {!!(
                              product?.ui?.text?.[`${key}Subtitle`] ||
                              OFFER_MATRIX_TITLES[
                                `${key}Subtitle` as keyof typeof OFFER_MATRIX_TITLES
                              ]
                            ) && (
                              <p className="py-2 text-xs text-zinc-500 italic dark:text-zinc-300">
                                {product?.ui?.text?.[`${key}Subtitle`] ||
                                  OFFER_MATRIX_TITLES[
                                    `${key}Subtitle` as keyof typeof OFFER_MATRIX_TITLES
                                  ]}
                              </p>
                            )}
                            <div
                              className={clsx(
                                'mb-4 mt-2 grid grid-cols-2 justify-items-stretch gap-3',
                                matrix.size === 3 && 'md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3',
                              )}
                            >
                              {Array.from(matrix).map(([optionKey, map]) => {
                                const { option, indexes } = map;

                                const kt = key as keyof typeof OFFER_MATRIX_MAP;
                                const offerMatrixMap = OFFER_MATRIX_MAP[kt];
                                const offerIndex = indexes.has(selectedIndex)
                                  ? selectedIndex
                                  : getBestOfferMatch(kt, matrix, optionKey, variety);

                                const offer = offers?.[offerIndex];
                                const over = option as API.MembershipProductOfferAddOn | undefined;

                                const thisOffer = offer || BASE_OFFER;

                                const isAddOn = kt === 'addOn' || kt === 'combos';
                                const remoteProductId = isAddOn ? over && over.productId : false;
                                const remoteOfferId = (over && over.offerId) || 0;
                                const remoteProduct =
                                  (!!remoteProductId && product?.products?.[remoteProductId]) ||
                                  undefined;
                                const remoteOffer =
                                  (remoteProduct && remoteProduct?.offers?.[remoteOfferId]) ||
                                  undefined;

                                const offerCalc = calculatePrice({
                                  discount,
                                  stats: stats || BASE_STATS,
                                  variety: thisOffer || BASE_OFFER,
                                });

                                const remoteCalc = calculatePrice({
                                  discount,
                                  stats: remoteProduct?.stats || BASE_STATS,
                                  variety: remoteOffer || BASE_OFFER,
                                });

                                const visiblePrice = isAddOn
                                  ? offerCalc.addOnPrice && offerCalc?.addOnPrice > 0
                                    ? formatNumber(offerCalc?.addOnPrice || 0, {
                                        maximumFractionDigits: 0,
                                      })
                                    : '-'
                                  : formatNumber(offerCalc.basePrice, {
                                      maximumFractionDigits: 0,
                                    });

                                let lastPriceIndex = curPriceIndex;

                                if (lastPrice !== visiblePrice) {
                                  if (lastPrice !== Infinity) {
                                    curPriceIndex++;
                                    lastPriceIndex++;
                                  }
                                  lastPrice = visiblePrice;
                                }

                                return (
                                  <Field key={optionKey}>
                                    <Radio as={Fragment} value={optionKey}>
                                      {({ checked }) => {
                                        // const priceDiff = offerCalc.price - (calcPrice.price || 0);

                                        return (
                                          <span
                                            className={clsx(
                                              // active
                                              //   ? 'ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-zinc-800'
                                              //   : '',
                                              checked
                                                ? 'bg-white shadow ring-zinc-500 dark:bg-white/10 dark:text-white'
                                                : 'bg-zinc-900/5 ring-zinc-300 ring-inset dark:bg-white/2.5 dark:hover:bg-white/5 hover:bg-zinc-900/2.5 dark:text-zinc-200 dark:ring-white/20',
                                              'flex size-full cursor-pointer flex-col items-start justify-between space-y-2 rounded p-3 font-semibold text-sm text-zinc-900 ring-1 sm:flex-1',
                                            )}
                                          >
                                            <div className="w-full">
                                              <div className="flex w-full flex-row items-center justify-between">
                                                <span>
                                                  {nestedReplace(
                                                    offer?.ui?.option?.[key]?.title ||
                                                      (over
                                                        ? over.ui?.title ||
                                                          offer?.ui?.title ||
                                                          offerMatrixMap?.[0]
                                                        : //TODO fix this properly
                                                          offerMatrixMap?.[1] || `No ${key}`),
                                                    over,
                                                    matrix,
                                                    remoteCalc || offerCalc,
                                                  )}
                                                </span>
                                                <CheckCircleIcon
                                                  className={clsx(
                                                    'ml-1 size-5 shrink-0 fill-emerald-700 dark:fill-emerald-500',
                                                    !checked && 'invisible',
                                                  )}
                                                />
                                              </div>
                                              <div className="mt-2 font-normal text-xs text-zinc-700 dark:text-zinc-200">
                                                {nestedReplace(
                                                  offer?.ui?.option?.[key]?.description ||
                                                    (over
                                                      ? over?.ui?.description ||
                                                        offer?.ui?.description
                                                      : offerMatrixMap?.[2] || `No ${key}`),
                                                  over,
                                                  matrix,
                                                  remoteCalc || offerCalc,
                                                )}
                                              </div>
                                            </div>
                                            <div>
                                              {discount ? (
                                                <div className="text-base font-normal text-zinc-500 line-through">
                                                  {isAddOn
                                                    ? offerCalc?.originalAddOnPrice
                                                      ? offerCalc?.originalAddOnPrice > 0 &&
                                                        formatNumber(
                                                          offerCalc?.originalAddOnPrice || 0,
                                                          {
                                                            maximumFractionDigits: 0,
                                                          },
                                                        )
                                                      : ''
                                                    : formatNumber(offerCalc.originalPrice, {
                                                        maximumFractionDigits: 0,
                                                      })}
                                                </div>
                                              ) : (
                                                ''
                                              )}
                                              <div
                                                className={clsx(
                                                  'text-base',
                                                  'hidden group-has-[[data-priceindex="1"]]:block',
                                                )}
                                                data-priceindex={lastPriceIndex}
                                              >
                                                {visiblePrice}
                                              </div>
                                            </div>
                                            {offer?.ui?.option?.[key]?.schedule ? (
                                              <div className="flex flex-row items-start space-x-2">
                                                <CalendarIcon className="size-4 shrink-0" />
                                                <span className="font-normal text-xs">
                                                  {nestedReplace(
                                                    offer?.ui?.option?.[key]?.schedule ||
                                                      (over
                                                        ? over?.ui?.schedule || offer?.ui?.schedule
                                                        : offerMatrixMap?.[2] || `No ${key}`),
                                                    over,
                                                    matrix,
                                                    remoteCalc || offerCalc,
                                                  )}
                                                </span>
                                              </div>
                                            ) : (
                                              ''
                                            )}
                                          </span>
                                        );
                                      }}
                                    </Radio>
                                  </Field>
                                );
                              })}
                            </div>
                          </RadioGroup>
                        );
                      })}

                    {product?.ui?.note ? (
                      <div className="mt-4 mb-6 rounded bg-amber-50 p-4 text-amber-700 sm:mt-10 dark:bg-amber-300/20 dark:text-amber-400">
                        <div className="flex space-x-2">
                          <InformationCircleIcon className="size-5 shrink-0" />
                          <div className="font-normal text-sm">{product?.ui?.note}</div>
                        </div>
                      </div>
                    ) : (
                      ''
                    )}

                    <div className="">
                      <PriceInfo
                        {...calcPrice}
                        loading={isLoading || !variety}
                        earlyBird={variety?.ui?.earlyBird}
                        frequencyEnabled={
                          variety?.ui?.frequencyEnabled || product?.ui?.frequencyEnabled
                        }
                        frequency={frequency}
                        locale={locale}
                        variety={variety}
                        stats={stats}
                        discount={discount}
                        balance={balance}
                        earlyBirdEndDate={variety?.ui?.earlyBirdEndDate}
                      />

                      {variety?.ui?.prorated ? (
                        <p className="mb-4 text-xs text-zinc-700 dark:text-zinc-200">
                          Prorated on {variety?.ui?.prorated}
                        </p>
                      ) : (
                        <></>
                      )}

                      <div className="grid grid-cols-1 gap-x-6 gap-y-4">
                        <StripeButton
                          product={product}
                          index={selectedIndex}
                          price={calcPrice.price}
                          size="large"
                          source={pageContext.urlPathname}
                        />
                        <a
                          href="/support-chat"
                          className="flex flex-row items-center text-xs text-zinc-500 dark:text-zinc-300"
                        >
                          <CrytpoIcon className="me-1 size-5 shrink-0" />
                          <span>Contact us to pay with cryptocurrency</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {disclaimer.summary && (
                <div className="order-last mt-10 lg:col-span-4">
                  <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                    Disclaimer
                  </h3>
                  <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
                    {disclaimer.summary}
                  </p>
                </div>
              )}
              {/* Product details */}
              <div className="mx-auto mt-10 max-w-2xl border-zinc-900/10 border-t pt-10 lg:col-span-3 lg:row-span-3 lg:mt-0 dark:border-zinc-50/10 lg:border-t-0 lg:p-0">
                {product?.ui?.whatToExpect && (
                  <div className="border-zinc-900/10 pb-6 dark:border-zinc-50/10 last:border-b">
                    <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                      What to expect
                    </h3>
                    <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                      <ul role="list">
                        {product.ui?.whatToExpect.map((highlight) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {product?.ui?.newThisYear && (
                  <div className="mb-10">
                    <h3 className="font-medium text-emerald-700 text-sm dark:text-zinc-100">
                      New for {product.ui?.newThisYear?.year}
                      <SparklesIcon className="ml-2 inline-block size-4 shrink-0" />
                    </h3>
                    <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                      <ul role="list">
                        {product.ui?.newThisYear?.list.map((highlight) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {product?.ui?.includes && (
                  <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 dark:border-zinc-50/10 first:border-t-0 first:pt-0">
                    <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                      Includes
                    </h3>
                    <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                      <ul role="list">
                        {product.ui?.includes.map((highlight) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {product?.ui?.highlights && (
                  <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 dark:border-zinc-50/10 first:border-t-0 first:pt-0">
                    <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      Highlights
                    </h3>
                    <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                      <ul role="list">
                        {product.ui?.highlights.map((highlight) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {hasBettingTalkExtra && (
                  <div className="mt-8 rounded bg-sky-50 p-4 text-sky-950 shadow-sm ring-1 ring-sky-900/10 dark:bg-sky-950 dark:text-sky-50 dark:ring-sky-50/5">
                    <span className="pb-2 text-sm font-medium">Added bonus</span>
                    <div className="mt-2 flex flex-row items-center justify-start space-x-4">
                      <BettingTalkIcon className="size-12 fill-sky-900 dark:fill-sky-200" />
                      <div>
                        <h4 className="font-semibold">Betting Talk membership</h4>
                        <p className="text-sm font-normal">(up to $295 value)</p>
                      </div>
                    </div>

                    <p className="mt-2 text-xs">
                      Ordering this service will create or extend your Betting Talk membership 12
                      months from order date.
                    </p>

                    <ul className="ml-4 mt-4 list-disc space-y-1 text-xs">
                      <li>Access to Betting Talk private community</li>
                      <li>65+ plays per year</li>
                      <li>All 2nd half CFB plays</li>
                    </ul>
                  </div>
                )}

                {product?.ui?.currentResults && (
                  <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 dark:border-zinc-50/10 first:border-t-0 first:pt-0">
                    <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      Current results
                    </h3>
                    <div className="prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                      <dl className="grid w-auto grid-flow-row grid-cols-1 gap-0 space-y-4 lg:grid-cols-1 lg:gap-0 lg:space-y-4">
                        {product?.ui?.currentResults.map(({ label, value, percentage, units }) => (
                          <div key={label} className="flex flex-col text-left">
                            <dt className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                              {label}
                            </dt>
                            <dd className="mt-2 p-0 text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
                              {value}{' '}
                              <span className="text-sm font-normal text-zinc-400">
                                ({percentage}%)
                              </span>
                              <span className="ms-2 w-fit rounded bg-emerald-100 px-2.5 py-0.5 text-sm font-medium text-emerald-900 dark:bg-emerald-400/20 dark:text-emerald-50">
                                {units} units
                              </span>
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>
                )}

                {product?.ui?.results && (
                  <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 dark:border-zinc-50/10 first:border-t-0 first:pt-0">
                    <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      Past results
                    </h3>
                    <div className="prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                      <dl className="grid w-auto grid-flow-row grid-cols-1 gap-0 space-y-4 lg:grid-cols-1 lg:gap-0 lg:space-y-4">
                        {product?.ui?.results.map(({ label, value, percentage, units }) => (
                          <div key={label} className="flex flex-col text-left">
                            <dt className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                              {label}
                            </dt>
                            <dd className="mt-2 p-0 text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
                              {value}{' '}
                              <span className="text-sm font-normal text-zinc-400">
                                ({percentage}%)
                              </span>
                              <span className="ms-2 w-fit rounded bg-emerald-100 px-2.5 py-0.5 text-sm font-medium text-emerald-900 dark:bg-emerald-400/20 dark:text-emerald-50">
                                {units} units
                              </span>
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                    {product?.ui?.resultsLink && (
                      <a
                        href={product?.ui?.resultsLink?.href}
                        target="_blank"
                        className="mt-6 inline-flex flex-row space-x-1 text-sm text-zinc-600 dark:text-zinc-300"
                        rel="noreferrer"
                      >
                        <span>{product?.ui?.resultsLink?.text || 'More results'}</span>
                        <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
                      </a>
                    )}
                    <div className="mt-6 text-xs text-zinc-500 sm:mt-10 dark:text-zinc-400">
                      Results monitored independently by{' '}
                      <a
                        href="https://betstamp.app/u/RAS"
                        target="_blank"
                        className="text-zinc-600 underline dark:text-zinc-300"
                        rel="noreferrer"
                      >
                        betstamp.
                      </a>
                    </div>
                  </div>
                )}

                {product?.ui?.faqs && product.ui.faqs.length > 0 && (
                  <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 dark:border-zinc-50/10 first:border-t-0 first:pt-0">
                    <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                      Frequently asked questions
                    </h3>
                    {product?.ui?.faqs?.map((faq) => (
                      <Fragment key={faq.question}>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">
                          <dt className="mt-10 font-medium text-zinc-900 dark:text-zinc-100">
                            {faq.question}
                          </dt>
                          <dd className="prose prose-sm mt-2 max-w-none text-zinc-500 dark:text-zinc-400">
                            <p>{faq.answer}</p>
                          </dd>
                        </div>
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductPage;
